import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Layers, Users, Sparkles, Store, Ruler, AlertCircle, Check, Heart } from "lucide-react";
import ShareButton from "./ShareButton";
import { useAuth } from "../context/AuthContext";
import { useLovedDeals } from "../context/LovedDealsContext";

// Per-collection collage built ONLY from this set's own verified product images. When an image
// isn't verified skin-free it falls back to a neutral, per-product text tile — never a shared hero.
// Standardized 4:3 stage on every breakpoint; each garment is fitted whole (object-contain), never cropped.
function Collage({ tiles, name }) {
  const t = (tiles && tiles.length ? tiles : [{ name, image: "" }]).slice(0, 4);
  const three = t.length === 3;
  const cols = t.length <= 1 ? "grid-cols-1" : "grid-cols-2";
  const rows = t.length > 2 ? "grid-rows-2" : "grid-rows-1";
  return (
    <div className={`relative w-full aspect-[4/3] grid gap-px bg-line ${cols} ${rows}`} data-testid="deal-collage">
      {t.map((tile, i) => (
        <div key={`${tile.id || tile.image || tile.name || "t"}-${i}`} className={`relative overflow-hidden bg-blush flex items-center justify-center min-h-0 ${three && i === 0 ? "row-span-2" : ""}`}>
          {tile.image ? (
            <img src={tile.image} alt={`${tile.name || name}${tile.color ? ` — ${tile.color}` : ""}`} loading="lazy" className="absolute inset-0 w-full h-full object-contain object-center p-2" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center px-2 text-center">
              <Layers size={18} className="text-brand/40 mb-1" />
              <span className="text-[10px] font-semibold text-ink-soft leading-tight line-clamp-2">{tile.name || "Product"}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function SizingStatus({ status, id, signedIn, familyReady }) {
  if (status === "full" && signedIn && familyReady === false) {
    return (
      <span data-testid={`deal-sizing-${id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-ink-soft bg-cream border border-line px-2.5 py-1 rounded-full">
        <Ruler size={11} /> Official chart guidance
      </span>
    );
  }
  if (status === "full") {
    return (
      <span data-testid={`deal-sizing-${id}`} className="inline-flex items-center gap-1 text-[11px] font-bold text-success bg-success/10 border border-success/20 px-2.5 py-1 rounded-full">
        <Ruler size={11} /> Brand size chart available
      </span>
    );
  }
  if (!signedIn) {
    return (
      <span data-testid={`deal-sizing-${id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand bg-brand-light/60 border border-brand/20 px-2.5 py-1 rounded-full">
        <Ruler size={11} /> Choose sizes using retailer charts
      </span>
    );
  }
  return (
    <span data-testid={`deal-sizing-${id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-ink-soft bg-cream border border-line px-2.5 py-1 rounded-full">
      <AlertCircle size={11} /> Exact brand chart not yet verified for every piece — compare the retailer's size chart
    </span>
  );
}

export const countsLine = (deal) => {
  const pieces = deal.placement_count != null ? deal.placement_count : (deal.items || []).length;
  const members = deal.family_member_count || 0;
  const parents = deal.distinct_parent_count || 0;
  const extras = deal.extras_count || 0;
  const main = pieces - extras;
  const a = members ? `${main} piece${main === 1 ? "" : "s"} for ${members} family member${members === 1 ? "" : "s"}` : `${main} piece${main === 1 ? "" : "s"}`;
  const b = extras ? ` + ${extras} optional extra${extras === 1 ? "" : "s"}` : "";
  const c = parents ? ` · ${parents} distinct product${parents === 1 ? "" : "s"}` : "";
  return a + b + c;
};
export const totalLabel = (deal) => (deal.complete_the_look || (deal.extras_count || 0) > 0 ? "Collection total" : "Complete outfit total");

export default function DealCard({ deal, canUnlock, onGetLinks, onPreview, testid, fits }) {
  const { user } = useAuth();
  const { lovedIds, loading: lovedLoading, toggle: toggleLoved } = useLovedDeals();
  const id = deal.id;
  const isLoved = lovedIds.has(id);
  const occasion = deal.occasion ? String(deal.occasion).replace(/_/g, " ") : "";
  const matchType = deal.complete_the_look ? "Complete the Look" : (deal.match_type === "exact_match" ? "Exact Match" : (deal.match_type ? "Coordinating" : ""));
  const productCount = deal.product_count != null ? deal.product_count : (deal.items || []).length; // eslint-disable-line no-unused-vars
  const retailerCount = deal.retailer_count != null ? deal.retailer_count : 0;
  const priceVerified = deal.price_verified && deal.total_price != null;
  const covered = deal.covered_children || [];
  const missing = deal.missing_children || [];
  const hasTargets = (covered.length + missing.length) > 0;
  const fitList = []; // Fit badges are retired; chart guidance never certifies fit.
  const shownFits = fitList.slice(0, 3);
  const moreFits = fitList.length - shownFits.length;

  return (
    <div
      data-testid={testid || `deal-card-${id}`}
      className="group bg-white rounded-3xl border border-line shadow-soft overflow-hidden hover:shadow-lift hover:-translate-y-1 transition-all duration-300 flex flex-col"
    >
      <div className="relative">
        <Collage tiles={deal.collage} name={deal.name} />
        {occasion && (
          <span className="absolute top-3 left-3 bg-white/95 backdrop-blur text-ink text-xs font-bold px-3 py-1.5 rounded-full shadow capitalize" data-testid={`deal-occasion-${id}`}>
            {occasion}
          </span>
        )}
        {matchType && (
          <span className="absolute top-3 left-1/2 -translate-x-1/2 bg-brand/90 text-white text-[11px] font-bold px-3 py-1.5 rounded-full shadow" data-testid={`deal-match-${id}`}>
            {matchType}
          </span>
        )}
        <div className="absolute top-3 right-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => toggleLoved(deal)}
            disabled={lovedLoading}
            aria-pressed={isLoved}
            aria-label={isLoved ? `Remove ${deal.name} from saved collections` : `Save this collection: ${deal.name}`}
            title={isLoved ? "Loved" : "Love this deal"}
            data-testid={`love-deal-${id}`}
            className={`w-11 h-11 rounded-full shadow flex items-center justify-center transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:opacity-60 ${isLoved ? "bg-brand text-white" : "bg-white/95 text-brand hover:bg-blush"}`}
          >
            <Heart size={20} fill={isLoved ? "currentColor" : "none"} aria-hidden="true" />
          </button>
          <ShareButton dealId={id} title={deal.name} price={priceVerified ? deal.total_price : 0} />
        </div>
      </div>

      <div className="p-5 flex flex-col gap-3 flex-1">
        <h3 className="font-heading font-extrabold text-lg text-ink leading-snug">{deal.name}</h3>

        {deal.completeness_label && (
          <span data-testid={`deal-completeness-${id}`} className="inline-flex items-center gap-1 w-fit bg-gold/15 text-gold-text text-[11px] font-bold px-2.5 py-1 rounded-full border border-gold/30">
            {deal.completeness_label}
          </span>
        )}

        {shownFits.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5" data-testid={`deal-fits-${id}`}>
            {shownFits.map((f) => (
              <span key={f.child_id} data-testid={`fits-badge-${id}-${f.child_id}`} className="inline-flex items-center gap-1 bg-success/10 text-success text-[11px] font-bold px-2.5 py-1 rounded-full border border-success/20 capitalize">
                <Sparkles size={11} /> Fits {f.nickname}
              </span>
            ))}
            {moreFits > 0 && (
              <span data-testid={`fits-more-${id}`} className="inline-flex items-center text-[11px] font-bold px-2 py-1 rounded-full bg-success/10 text-success border border-success/20">+{moreFits} more</span>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 text-sm text-ink-soft">
          <span className="inline-flex items-center gap-1.5" data-testid={`deal-pieces-${id}`}>
            <Layers size={15} className="text-brand" /> {countsLine(deal)}
          </span>
          <span className="text-line">·</span>
          <span className="inline-flex items-center gap-1.5" data-testid={`deal-retailers-${id}`}>
            <Store size={15} className="text-brand" /> {retailerCount} retailer{retailerCount === 1 ? "" : "s"}
          </span>
        </div>

        <SizingStatus status={deal.sizing_status} id={id} signedIn={!!user}  familyReady={deal.family_sizing_ready} />

        {hasTargets && (
          <div className="text-xs" data-testid={`deal-coverage-${id}`}>
            {covered.length > 0 && (
              <p className="flex items-start gap-1.5 text-ink-soft">
                <Check size={13} className="text-success mt-0.5 shrink-0" />
                <span><span className="font-bold text-ink">Covers:</span> <span className="capitalize">{covered.join(", ")}</span></span>
              </p>
            )}
            {missing.length > 0 && (
              <p className="flex items-start gap-1.5 text-brand mt-1" data-testid={`deal-missing-${id}`}>
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                <span><span className="font-bold">Not covered:</span> <span className="capitalize">{missing.join(", ")}</span></span>
              </p>
            )}
          </div>
        )}

        <div className="mt-auto pt-3 border-t border-line flex items-end justify-between gap-2">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-ink-muted" data-testid={`deal-total-label-${id}`}>{totalLabel(deal)}</p>
            {priceVerified ? (
              <>
                <p className="font-heading font-black text-2xl text-ink" data-testid={`deal-price-${id}`}>${deal.total_price.toFixed(2)}</p>
                {(deal.extras_count || 0) > 0 ? (
                  <p className="text-[11px] text-ink-muted mt-0.5" data-testid={`deal-subtotals-${id}`}>Main outfits ${Number(deal.main_outfit_total || 0).toFixed(2)} · Optional extras ${Number(deal.extras_total || 0).toFixed(2)}</p>
                ) : (
                  <p className="text-[11px] text-ink-muted mt-0.5">Verified across {retailerCount} retailer{retailerCount === 1 ? "" : "s"}</p>
                )}
              </>
            ) : (
              <p className="font-heading font-bold text-base text-brand" data-testid={`deal-price-unconfirmed-${id}`}>Price confirmation needed</p>
            )}
          </div>
          <Link
            to={`/deal/${id}`}
            onClick={(e) => {
              if (canUnlock && onGetLinks) { e.preventDefault(); onGetLinks(deal); }
              else if (!canUnlock && onPreview) { e.preventDefault(); onPreview(deal); }
            }}
            aria-label={`View ${deal.name} collection`}
            data-testid={canUnlock ? `get-links-btn-${id}` : `locked-btn-${id}`}
            className={`flex items-center gap-1.5 h-12 px-5 rounded-full font-bold text-sm active:scale-95 transition-all shrink-0 ${
              canUnlock ? "bg-brand text-white hover:bg-brand-hover shadow-md" : "bg-blush text-brand border border-brand/20 hover:bg-brand-light"
            }`}
          >
            View the collection <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </div>
  );
}
